if (!customElements.get('product-gallery-controller')) {
  customElements.define(
    'product-gallery-controller',
    class ProductGalleryController extends HTMLElement {
      static get observedAttributes () {
        return ['data-force-muted'];
      }

      constructor () {
        super();
        this.selectors = {
          'allMedia': '[thumbnail-alt]',
          'videoMedia': '[thumbnail-alt]:has(> video-media video)',
          'videos': '[thumbnail-alt] > video-media video',
        };
        this.productHandle = this.getAttribute('data-product-handle');
        this.selectedVariantID = this.getAttribute('data-selected-variant-id');
        this.forceMuted = this.hasAttribute('data-force-muted');
        const data = this.querySelector(`*[data-type="media-rules"]`)?.textContent;
        this.rules = null;
        this.product = null;

        this.setVolumeForAllVideoHandler = this.setVolumeForAllVideo.bind(this);
        this.tepoOptions = [];
        this._bridgeActive = false;
        this._bridgeAC = null;
        this._heavyInitialized = false;
        this._onResize = this._handleResize.bind(this);
        this.root = this.closest('div');
        this.productForm = this.root?.querySelector('.shopify-product-form');
        this.temoOnchage = null;
        this.tepoObserver = null;
        try {
          if (data) {
            this.rules = JSON.parse(data);
          }
        } catch (e) {
          console.error(e.message);
        }
        this.documentEvents = {
          'desktop': [],
          'tablet': [
            'touchstart',
          ],
        };
      }

      attributeChangedCallback (name, oldVal, newVal) {
        if (name === 'data-force-muted') {
          this.forceMuted = this.hasAttribute('data-force-muted');
          this.applyForceMuted();
        }
      }

      applyForceMuted () {
        const videos = this.querySelectorAll('video');
        videos.forEach((v) => {
          const media = v.closest('video-media');
          if (this.forceMuted) {
            v.muted = true;
            v.volume = 0;
            if (media) {
              media.setAttribute('muted', '');
              media.removeAttribute('unmuted');
            }
            const tryPlay = () => v.play().catch(() => {});
            (v.readyState >= 2) ? tryPlay() : v.addEventListener('loadeddata', tryPlay, { once: true });
          } else {
            if (media && v.muted === false && v.volume > 0) {
              media.setAttribute('unmuted', '');
              media.removeAttribute('muted');
            }
          }
        });
      }

      _isGalleryHidden () {
        const pg = this.querySelector('.product-gallery');
        return !!(pg && pg.offsetParent === null);
      }

      _dehydrateMedia () {
        const TINY_GIF = 'data:image/gif;base64,R0lGODlhAQABAAAAACw=';
        this.querySelectorAll('img').forEach(img => {
          if (!img.dataset.src && img.src) img.dataset.src = img.src;
          if (!img.dataset.srcset && img.srcset) img.dataset.srcset = img.srcset;
          if (!img.dataset.sizes && img.sizes) img.dataset.sizes = img.sizes;

          img.srcset = '';
          img.sizes = '';
          img.src = TINY_GIF;
          img.loading = 'lazy';
          img.decoding = 'async';
          img.fetchPriority = 'low';
        });
      }

      _rehydrateMedia () {
        this.querySelectorAll('img').forEach(img => {
          if (img.dataset.src) {
            img.src = img.dataset.src;
            delete img.dataset.src;
          }
          if (img.dataset.srcset) {
            img.srcset = img.dataset.srcset;
            delete img.dataset.srcset;
          }
          if (img.dataset.sizes) {
            img.sizes = img.dataset.sizes;
            delete img.dataset.sizes;
          }
        });
      }

      _swapVideosToHidden () {
        this.querySelectorAll('video').forEach((video) => {
          const hidden = document.createElement('video-hidden');

          for (const { name, value } of Array.from(video.attributes)) {
            hidden.setAttribute(name, value);
          }
          hidden.setAttribute('data-swapped-from', 'video');
          while (video.firstChild) hidden.appendChild(video.firstChild);
          video.replaceWith(hidden);
        });
      }

      _swapHiddenToVideos () {
        this.querySelectorAll('video-hidden[data-swapped-from="video"]').forEach((hidden) => {
          const video = document.createElement('video');
          for (const { name, value } of Array.from(hidden.attributes)) {
            if (name !== 'data-swapped-from') video.setAttribute(name, value);
          }
          while (hidden.firstChild) video.appendChild(hidden.firstChild);

          hidden.replaceWith(video);
        });
      }

      _activateVariantBridge () {
        if (this._bridgeActive) return;
        const formId = this.getAttribute('form') || this.productForm?.id;
        this._bridgeAC = new AbortController();
        this._swapVideosToHidden();
        this._dehydrateMedia();
        this._bridgeVariantChange = (e) => {
          if (formId && e?.detail?.formId && e.detail.formId !== formId) return;
          const v = e?.detail?.variant;
          if (!v?.id) return;
          document.dispatchEvent(new CustomEvent('VariantChanged', {
            detail: { variantID: Number(v.id), variant: v },
          }));
        };

        this._bridgeProductRerender = () => {
          const form = formId ? document.forms[formId] : this.productForm;
          const id = form?.querySelector('input[name="id"]')?.value;
          if (!id) return;
          document.dispatchEvent(new CustomEvent('VariantChanged', {
            detail: { variantID: Number(id), variant: { id: Number(id) } },
          }));
        };

        document.addEventListener('variant:change', this._bridgeVariantChange, { signal: this._bridgeAC.signal });
        document.addEventListener('product:rerender', this._bridgeProductRerender, { signal: this._bridgeAC.signal });
        this._bridgeActive = true;
      }

      _deactivateVariantBridge () {
        if (!this._bridgeActive) return;
        this._bridgeAC?.abort();
        this._bridgeActive = false;
        this._swapHiddenToVideos();
        this._rehydrateMedia();
        const visibleCells = Array.from(this.querySelectorAll(this.selectors.allMedia)).
          filter(el => getComputedStyle(el).display !== 'none');

        visibleCells.forEach((cell, idx) => {
          const v = cell.querySelector('video');
          if (!v) return;
          v.setAttribute('playsinline', '');

          if (this.forceMuted) {
            v.muted = true;
            v.volume = 0;
          } else {
            v.muted = true;
            if (idx === 0) v.muted = false;
            if (idx > 0) v.volume = 0;
          }
          const tryPlay = () => { try { v.play().catch(() => {}); } catch {} };
          if (v.readyState >= 2) tryPlay();
          else {
            v.addEventListener('loadeddata', tryPlay, { once: true });
            v.addEventListener('loadedmetadata', tryPlay, { once: true });
          }
          const media = v.closest('video-media');
          if (media) {
            if (this.forceMuted) {
              media.setAttribute('muted', '');
              media.removeAttribute('unmuted');
            } else if (idx === 0) {
              media.setAttribute('unmuted', '');
              media.removeAttribute('muted');
            }
          }
        });
      }

      async _initHeavy () {
        if (this._heavyInitialized) return;
        this._heavyInitialized = true;
        if (this.productHandle && this.querySelectorAll(this.selectors.allMedia).length) {
          const response = await fetch(`${window.Shopify.routes.root}products/${this.productHandle}.js`);
          this.product = await response.json();
          if (this.selectedVariantID) {
            this.selectedVariantID = this.selectedVariantID * 1;
            if (this.product) {
              this.setTepoOptions();
              this.render();
              document.addEventListener('VariantChanged', this.variantChangedHandle.bind(this));

              this.detectTabletBreakpoint((state) => {
                const documentEvents = this.documentEvents;
                if (state) {
                  this.querySelector('.product-gallery__carousel').
                    addEventListener('carousel:change', this.carouselChangedHandle.bind(this));
                  documentEvents.tablet.forEach((event) => {
                    document.addEventListener(event, this.setVolumeForAllVideoHandler);
                  });
                } else {
                  documentEvents.tablet.forEach((event) => {
                    document.removeEventListener(event, this.setVolumeForAllVideoHandler);
                  });

                }
              });
            }
            if (this.productForm) {
              this.tepoObserver = this.waitForElement('.tcustomizer-widget', (element) => {
                if (!this.tepoOptions.hasOwnProperty('null')) {
                  const input = element.querySelector('.tcustomizer-data > input');
                  if (input) {
                    for (const option in this.tepoOptions) {
                      this.tepoOptions[option] = input.value;
                      this.render();
                      break;
                    }
                  }
                }
                this.temoOnchage = this.observeAttributeChanges(element, '.tcustomizer-data > input',
                  (attr, value, target) => {
                    let key = target.getAttribute('name');
                    if (key) {
                      key = key.match(/\[(.*?)\]/);
                      key = key ? key[1] : null;
                    }
                    if (key) {
                      this.tepoOptions[key] = value;
                    } else {
                      for (const option in this.tepoOptions) {
                        this.tepoOptions[option] = value;
                        break;
                      }
                    }
                    this.render();
                    if (this.matchMedia.matches) {
                      this.setVolumeForAllVideo();
                    }
                  });
              });
            }
          }
        }
        this.querySelectorAll(this.selectors.videos).forEach((item) => {
          const media = item.closest('video-media');
          if (media) {
            if (this.forceMuted) {
              item.muted = true;
              item.volume = 0;
              media.setAttribute('muted', '');
              media.removeAttribute('unmuted');
            } else {
              if (item.muted) {
                media.setAttribute('muted', '');
                media.removeAttribute('unmuted');
              } else {
                media.setAttribute('unmuted', '');
                media.removeAttribute('muted');
              }
            }
            const volumeControl = media.querySelector('.volume-controls');
            if (volumeControl) {
              volumeControl.addEventListener('click', this.volumeControl.bind(this));
              volumeControl.addEventListener('touchstart', (e) => {e.stopPropagation();});
            }
          }
          item.addEventListener('volumechange', this.volumeChange.bind(this));
        });
      }

      _handleResize () {
        if (this._resizeRaf) cancelAnimationFrame(this._resizeRaf);
        this._resizeRaf = requestAnimationFrame(async () => {
          const hidden = this._isGalleryHidden();
          if (hidden && !this._bridgeActive) {
            this._activateVariantBridge();
          } else if (!hidden && this._bridgeActive) {
            this._deactivateVariantBridge();
            await this._initHeavy();
          }
        });
      }

      get matchMedia () {
        return window.matchMedia('(max-width: 999px)');
      }

      /**
       * *
       * @param callback
       */
      detectTabletBreakpoint (callback) {
        const mediaQuery = this.matchMedia;
        let previousState = mediaQuery.matches;

        callback(previousState);

        let resizeTimeout;

        const resizeHandler = () => {
          clearTimeout(resizeTimeout);
          resizeTimeout = setTimeout(() => {
            let currentState = mediaQuery.matches;

            if (currentState !== previousState) {
              callback(currentState);
              previousState = currentState;
            }
          }, 200);
        };

        mediaQuery.addEventListener('change', resizeHandler);
        window.addEventListener('resize', resizeHandler);

        this.mediaQuery = mediaQuery;
        this.resizeHandler = resizeHandler;
      }

      /**
       * *
       * @param selector
       * @param callback
       * @returns {MutationObserver}
       */
      waitForElement (selector, callback) {
        const observer = new MutationObserver((mutations, obs) => {
          const element = document.querySelector(selector);
          if (element) {
            callback(element);
            obs.disconnect();
          }
        });
        observer.observe(document.body, { childList: true, subtree: true });
        return observer;
      }

      parseUrlParams () {
        return Object.fromEntries(new URLSearchParams(window.location.search));
      }

      /**
       * *
       * @param preparedOptions
       * @param mediaAlt
       * @returns {boolean}
       */
      findByOptions (preparedOptions, mediaAlt) {
        let foundAllOptions = true;
        for (const option of preparedOptions) {
          if (!mediaAlt.includes(option)) {
            foundAllOptions = false;
            break;
          }
        }
        return foundAllOptions;
      }

      /**
       * *
       * @returns {string}
       */
      getUrlRoot () {
        return `${window.location.hostname}/${this.trimSlashes(window.Shopify.routes.root)}`;
      }

      /**
       * *
       * @param rules
       * @returns {*}
       */
      findByUrlAll (rules) {
        const urlParams = this.parseUrlParams();
        const valuesSet = new Set(
          Object.entries(urlParams).flatMap(([key, value]) => [
            value.toLowerCase(),
            `${key}=${value.toLowerCase()}`,
          ]),
        );
        const root = this.getUrlRoot();
        valuesSet.add(root);
        return rules.every(rule => valuesSet.has(rule.toLowerCase()));
      }

      /**
       * *
       * @param rules
       * @returns {boolean}
       */
      findByUrl (rules) {
        const urlParams = this.parseUrlParams();
        const values = Object.entries(urlParams);
        const root = this.getUrlRoot();
        values.push(['root', root]);
        return values.some(([key, value]) => {
          value = value.toLowerCase();
          return rules.some((rule) => rule === value) || rules.includes(`${key}=${value}`);
        });
      }

      isVideo (item) {
        return (item.getAttribute('data-media-type') === 'video' || item.getAttribute('data-media-type') ===
          'external_video');
      }

      /**
       * *
       * @param item
       * @param show
       */
      processMedia (item, show) {
        if (this.isVideo(item)) {
          const video = item.firstElementChild;
          // Thumbnail/dot buttons always render a static <img> preview even
          // for video media (only the main carousel slide embeds a real
          // <video-media>/<video>), so guard against calling .play()/.pause()
          // on a non-video element -- doing so used to throw and silently
          // abort the rest of this render loop, hiding every subsequent
          // thumbnail after the first video-type one.
          if (video && typeof video.play === 'function' && typeof video.pause === 'function') {
            if (show) {
              video.play();
            } else {
              video.pause();
            }
          }
        }
      }

      /**
       * *
       * @param mediaAlt
       * @returns {{hide: *[], display: *[]}}
       */
      getUrlRules (mediaAlt) {
        const preparedMediaAlt = this.normalizeString(mediaAlt);
        const parts = preparedMediaAlt.split(',').map(part => part.trim());
        const result = { hide: [], display: [] };
        for (const type in result) {
          for (const part of parts) {
            const prefix = `${type}:`;
            if (part.startsWith(prefix)) {
              const value = part.replace(prefix, '').trim();
              if (value) {
                result[type].push(value);
              }
            }
          }
        }
        return result;
      }

      trimSlashes (str) {
        return str.replace(/^\/+|\/+$/g, '');
      }

      getFeaturedOutOfStockImages (preparedOptions) {
        let images = {
          'default': null,
          'localized': null,
        };
        for (const item of this.allGalleryMedia) {
          const img = item.querySelector('img:first-child');
          const rootPath = this.getUrlRoot();
          if (img) {
            const mediaAlt = item.getAttribute('thumbnail-alt').toLowerCase();
            let isRender = this.findByOptions(preparedOptions, mediaAlt);
            const urlRules = this.getUrlRules(mediaAlt);
            const isOOSImageDefault = mediaAlt.includes('oos_message_default');
            if (isRender) {
              if (isOOSImageDefault) {
                images.default = item;
              } else if (mediaAlt.includes('oos_message')) {
                if (urlRules.display.length && urlRules.display.some((rule) => rule === rootPath)) {
                  images.localized = item;
                }
              }
            }
          }
        }
        return images;
      }

      render () {
        const medias = this.querySelectorAll(this.selectors.allMedia);
        this.allGalleryMedia = [];
        const presaleMedias = this.querySelectorAll(`.product-gallery__media[data-pre-sale-featured]`);
        if (presaleMedias.length) {
          this.preSaleMedia = this.querySelector(`.product-gallery__media[data-pre-sale-featured][data-variant-id="${this.selectedVariantID}"]`);
          if (this.preSaleMedia) {
            medias.forEach(item => {
              item.style.display = 'none';
              this.processMedia(item, false);
            });
            this.preSaleMedia.style.display = 'block';
            this.processMedia(this.preSaleMedia, true);
            this.allGalleryMedia = [
              this.preSaleMedia,
              ...Array.from(medias).filter(item => item !== this.preSaleMedia),
            ];
            return;
          }
        }

        if (!this.allGalleryMedia.length) {
          this.allGalleryMedia = Array.from(medias);
        }
        const variant = this.product?.variants.find((variant) => variant.id === this.selectedVariantID);
        if (!variant) {
          return;
        }
        const featuredMediaAlt = variant?.featured_media?.alt ?? null;
        const preparedOptions = variant.options.map((item) => item.toLowerCase());
        const oosIMages = this.getFeaturedOutOfStockImages(preparedOptions);
        const defaultOOSIMage = oosIMages.localized ? oosIMages.localized : oosIMages.default;
        let defaultOOSIMageControl = null;
        const nav = this.querySelector('carousel-navigation');
        if (defaultOOSIMage) {
          const defaultOOSIMageMediaID = defaultOOSIMage.getAttribute('data-media-id');
          if (defaultOOSIMageMediaID) {
            defaultOOSIMageControl = this.querySelector(`carousel-navigation *[data-media-id="${defaultOOSIMage.getAttribute(
                    'data-media-id')}"]`);
          }
        }

        this.allGalleryMedia.forEach(item => {
          const currentVariantID = item.hasAttribute('data-pre-sale-featured') && item.hasAttribute('data-variant-id')
            ? item.getAttribute('data-variant-id') * 1
            : null;
          const show = (currentVariantID === this.selectedVariantID * 1)
            || !featuredMediaAlt
            || !preparedOptions.length;
          item.style.display = show ? 'block' : 'none';
        });

        const tepoOptions = [];

        for (const tepoOption in this.tepoOptions) {
          if (this.tepoOptions.hasOwnProperty(tepoOption) && this.tepoOptions[tepoOption]) {
            tepoOptions.push(this.tepoOptions[tepoOption].toLowerCase());
          }
        }
        let renderedIndex = false;
        if (featuredMediaAlt) {
          this.allGalleryMedia.forEach((item) => {
            const currentVariantID = item.hasAttribute('data-pre-sale-featured') && item.hasAttribute('data-variant-id')
              ? item.getAttribute('data-variant-id') * 1
              : null;
            if (currentVariantID === this.selectedVariantID * 1) {
              item.style.display = 'block';
              this.processMedia(item, true);
              return;
            }
            const mediaAlt = item.getAttribute('thumbnail-alt').toLowerCase();
            let isRender = this.findByOptions(preparedOptions, mediaAlt);

            if (isRender) {
              if (this.rules) {
                if (this.rules.hasOwnProperty('isThumbnails')) {
                  for (const rule of this.rules.isThumbnails) {
                    if (mediaAlt.includes(rule)) {
                      isRender = false;
                      break;
                    }
                  }
                }
                if (this.rules.hasOwnProperty('isOutOfStockFeaturedImages')) {
                  for (const rule of this.rules.isOutOfStockFeaturedImages) {
                    if (mediaAlt.includes(rule) &&
                      (variant.available || (item !== defaultOOSIMage && item !== defaultOOSIMageControl))) {
                      isRender = false;
                      break;
                    }
                  }
                }
              }
            }
            if (!isRender && tepoOptions.length) {
              isRender = this.findByOptions(tepoOptions, mediaAlt);
            }

            const urlRules = this.getUrlRules(mediaAlt);

            if (isRender && urlRules.display.length) {
              isRender = this.findByUrlAll(urlRules.display);
            }

            if (isRender && urlRules.hide.length) {
              isRender = !this.findByUrl(urlRules.hide);
            }

            if (isRender && !renderedIndex &&
              (item.firstElementChild.tagName === 'IMG' || item.firstElementChild.tagName === 'VIDEO-MEDIA')) {
              renderedIndex = true;
              if (!variant.available) {
                if (defaultOOSIMage) {
                  const parent = item.closest('.product-gallery__carousel');
                  if (parent) {
                    defaultOOSIMage.style.display = 'block';
                    defaultOOSIMage.classList.add('is--initial');
                    item.classList.remove('is--initial');
                    isRender = false;
                    parent.insertBefore(defaultOOSIMage, item);

                    if (nav) {
                      const oldItem = nav.querySelector(`[data-media-id="${item.getAttribute('data-media-id')}"]`);
                      oldItem.setAttribute('needle-to-hide', '');
                      if (defaultOOSIMageControl && oldItem) {
                        defaultOOSIMageControl.style.display = 'block';
                        nav.insertBefore(defaultOOSIMageControl, oldItem);
                      }
                    }
                  }

                }
              }
            }

            if (item.hasAttribute('needle-to-hide')) {
              isRender = false;
              item.removeAttribute('needle-to-hide');
            }
            item.style.display = isRender ? 'block' : 'none';
            this.processMedia(item, isRender);
          });
          if (nav) {
            nav.connectedCallback();
            const firstItem = nav.querySelector('[thumbnail-alt][style="display: block;"]');
            if (firstItem) {
              firstItem.dispatchEvent(new Event('click'));
              firstItem.setAttribute('aria-current', 'true');
            }
          }
        }

      }

      /**
       * *
       * @param e
       */
      setVolumeForAllVideo (e) {
        if (e?.type && this.documentEvents.tablet.includes(e.type)) {
          document.removeEventListener(e.type, this.setVolumeForAllVideoHandler);
        }
        if (this.forceMuted) {
          const active = [...this.querySelectorAll(this.selectors.allMedia)].filter(
            el => window.getComputedStyle(el).display === 'block');
          active.forEach((cell) => {
            const v = cell.querySelector('video');
            if (!v) return;
            v.muted = true;
            v.volume = 0;
            const tryPlay = () => v.play().catch(() => {});
            (v.readyState >= 2) ? tryPlay() : v.addEventListener('loadeddata', tryPlay, { once: true });
            const media = v.closest('video-media');
            if (media) {
              media.setAttribute('muted', '');
              media.removeAttribute('unmuted');
            }
          });
          return;
        }
        const allVideos = Array.from(this.querySelectorAll(this.selectors.allMedia));
        const activeVideos = allVideos.filter(el => window.getComputedStyle(el).display === 'block');
        this.muteAllVideo(null, false);
        activeVideos.forEach((activeVideo, index) => {
          const video = activeVideo.querySelector('video');
          if (video) {
            if (index > 0) {
              video.volume = 0;
              video.play().catch(error => console.warn('Autoplay blocked:', error));
              video.pause();
              video.volume = 1;
            } else {
              video.volume = 1;
              video.muted = false;
              video.play().catch(error => console.warn('Autoplay blocked:', error));
            }
          }
        });
      }

      /**
       *
       * @param input
       * @returns {*}
       */
      normalizeString (input) {
        return input.replace(/\s*(hide|display)\s*:\s*/g, '$1:');
      }

      /**
       *
       * @param event
       */
      variantChangedHandle (event) {
        this.selectedVariantID = event.detail.variantID;
        this.render();
        const isTablet = this.matchMedia.matches;
        if (this.forceMuted) {
          this.applyForceMuted();
        } else {
          if (!isTablet) this.muteAllVideo();
          else this.setVolumeForAllVideo();
        }
      }

      setTepoOptions () {
        try {
          let storage = localStorage.getItem('tcustomizer-customizations');
          if (storage) {
            storage = JSON.parse(storage);
            if (storage) {
              const productData = storage.find((item) => item.productId === this.product.id);
              if (productData && productData.hasOwnProperty('data')) {
                this.tepoOptions = productData.data;
              }
            }
          }
        } catch (e) {
          console.error(e.message);
        }

      }

      observeAttributeChanges (root, selector, callback) {
        const target = root.querySelector(selector);
        if (!target) {
          console.warn(`Element not found: ${selector}`);
          return;
        }

        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'attributes') {
              callback(mutation.attributeName, target.getAttribute(mutation.attributeName), target);
            }
          });
        });

        observer.observe(target, { attributes: true });

        return observer;
      }

      carouselChangedHandle (e) {
        const element = e.detail.cell;
        if (this.isVideo(element)) {
          const video = element.querySelector('video');
          video.pause();
          if (this.forceMuted) {
            video.muted = true;
            video.volume = 0;
          } else {
            video.muted = false;
            video.volume = 1;
          }
          video.play().catch(error => console.warn('Autoplay blocked:', error));
        } else {
          this.muteAllVideo(null, false);
        }
      }

      async connectedCallback () {
        window.addEventListener('resize', this._onResize, { passive: true });
        if (this._isGalleryHidden()) {
          this._activateVariantBridge();
          return;
        }
        await this._initHeavy();
      }

      muteAllVideo (current = null, play = true) {
        this.querySelectorAll(this.selectors.videos).forEach((item) => {
          if (item !== current && (!item.muted && item.volume > 0)) {
            item.pause();
            item.volume = 0;
            item.muted = true;
            if (play) {
              item.play().catch(error => console.warn('Autoplay blocked:', error));
            }
          }
        });
      }

      volumeChange (e) {
        const video = e.target.closest('video');
        this.muteAllVideo(video);

        const media = video.closest('video-media');

        if (media) {
          if (video.muted || video.volume === 0) {
            media.removeAttribute('unmuted');
            media.setAttribute('muted', '');
          } else {
            media.removeAttribute('muted');
            media.setAttribute('unmuted', '');
          }
        }
      }

      volumeControl (e) {
        e.preventDefault();
        e.stopPropagation();
        const media = e.target.closest('video-media');
        if (media) {
          const video = media.querySelector('video');
          if (video) {
            video.pause();
            video.muted = !video.muted;
            if (video.muted) {
              video.volume = 0;
            } else {
              video.volume = 1;
            }
            video.play().catch(error => console.warn('Autoplay blocked:', error));
          }
        }
      }

      disconnectedCallback () {
        window.removeEventListener('resize', this._onResize);
        this._deactivateVariantBridge();
        if (this.mediaQuery && this.resizeHandler) {
          this.mediaQuery.removeEventListener('change', this.resizeHandler);
          window.removeEventListener('resize', this.resizeHandler);
        }

        document.removeEventListener('VariantChanged', this.variantChangedHandle.bind(this));

        this.documentEvents.tablet.forEach((event) => {
          document.removeEventListener(event, this.setVolumeForAllVideoHandler);
        });
        this.documentEvents.desktop.forEach((event) => {
          document.removeEventListener(event, this.setVolumeForAllVideoHandler);
        });

        const carousel = this.querySelector('.product-gallery__carousel');
        if (carousel) {
          carousel.removeEventListener('carousel:change', this.carouselChangedHandle.bind(this));
        }

        if (this.temoOnchage) {
          this.temoOnchage.disconnect();
        }
        if (this.tepoObserver) {
          this.tepoObserver.disconnect();
        }

        this.tepoOptions = [];
      }
    },
  );
}